// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useNavigate } from 'react-router-dom'
import { useState, useEffect, useCallback } from 'react'
import { Clock, CheckCircle2 } from 'lucide-react'
import { SaveStatusBadge } from '@/components/ui/SaveStatusBadge'
import { PrimaryActionButton } from '@/components/ui/PrimaryActionButton'
import { SecondaryActionButton } from '@/components/ui/SecondaryActionButton'
import { ModuleShell }     from '@/components/ui/ModuleShell'
import { Textarea, Input } from '@/components/ui'
import { Card }            from '@/components/ui/Card'
import { LoadingState }    from '@/components/feedback/LoadingState'
import { useAuthStore }    from '@/store'
import { ROUTES }     from '@/lib/constants/routes'
import { useReturnNav } from '@/lib/returnNav'
import { useAutosave }     from '@/hooks/useAutosave'
import { loadMunicipality, saveMunicipality, EMPTY_MUNICIPALITY, type MunicipalityData } from '@/services/wishes'

const TIMELINE = [
  {
    period: 'Direct regelen',
    color:  'bg-status-alertBg border-status-alertBorder text-status-alertText',
    dot:    'bg-red-400',
    items: [
      'Huisarts of ziekenhuis laat overlijden vaststellen',
      'Aangifte van overlijden bij de gemeente (kan de uitvaartverzorger doen)',
      'Uitvaartverzorger regelen indien gewenst',
      'Directe familie en naasten informeren',
    ],
  },
  {
    period: 'Binnen enkele dagen',
    color:  'bg-status-warnBg border-status-warnBorder text-status-warnText',
    dot:    'bg-yellow-400',
    items: [
      'Akte van overlijden ontvangen - vraag minimaal 5 officiële afschriften aan',
      'Bank en spaarrekening blokkeren en nalatenschap melden',
      'Verzekeraar(s) informeren',
      'Werkgever informeren',
      'DigiD van de overledene vervalt automatisch',
      'Huurcontract of hypotheek melden',
    ],
  },
  {
    period: 'Binnen enkele weken',
    color:  'bg-status-infoBg border-sage-200 text-status-infoText',
    dot:    'bg-blue-400',
    items: [
      'Pensioenfonds informeren voor nabestaandenpensioen',
      'Belastingdienst informeren (aangifte inkomstenbelasting lopend jaar)',
      'Abonnementen en lidmaatschappen opzeggen',
      'Wmo-hulpmiddelen teruggeven aan gemeente',
      'Parkeervergunning inleveren',
      'Gemeentelijke belastingen melden',
    ],
  },
  {
    period: 'Afwikkeling nalatenschap',
    color:  'bg-sage-50 border-sage-200 text-sage-700',
    dot:    'bg-sage-400',
    items: [
      'Notaris inschakelen voor afwikkeling nalatenschap',
      'Erfenis aanvaarden of verwerpen - raadpleeg de notaris over de voor u geldende termijn',
      'Boedelbeschrijving opstellen',
      'Erfbelasting voorbereiden - aangifte uiterlijk 8 maanden na overlijden',
    ],
  },
  {
    period: 'Afwikkeling nalatenschap - afronding',
    color:  'bg-sand-200 border-sand-300 text-ink-3',
    dot:    'bg-ink-4',
    items: [
      'Aangifte erfbelasting indienen bij Belastingdienst (termijn: uiterlijk 8 maanden na overlijden; verlenging mogelijk op aanvraag)',
      'Verdeling nalatenschap afronden',
      'Afsluitende zaken bij bank, notaris en overheid',
    ],
  },
]

export function MunicipalityPage() {
  const { userId }  = useAuthStore()
  const navigate   = useNavigate()
  const { target, label } = useReturnNav()
  const [data,      setData]      = useState<MunicipalityData>(EMPTY_MUNICIPALITY)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!userId) return
    loadMunicipality(userId).then(loaded => {
      if (loaded) setData(loaded)
      setIsLoading(false)
    })
  }, [userId])

  const save = useCallback(async (d: MunicipalityData) => {
    if (!userId) return
    await saveMunicipality(userId, d)
  }, [userId])

  const { status: saveStatus, saveNow } = useAutosave({ data, onSave: save, enabled: !isLoading })
  const upd = useCallback((k: keyof MunicipalityData, v: string) => setData(prev => ({ ...prev, [k]: v })), [])

  if (isLoading) return <LoadingState message="Laden..." />

  return (
    <div>
        <button onClick={async () => { await saveNow(); navigate(ROUTES.DASHBOARD) }}
          className="flex items-center gap-1.5 text-sm text-ink-4 hover:text-ink-3 transition-colors mb-5 group">
          <svg className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
          Terug naar dashboard
        </button>
      <div className="flex items-start gap-3 rounded-xl bg-status-infoBg border border-sage-200 px-4 py-3">
        <span className="text-sm flex-shrink-0">ℹ️</span>
        <p className="text-xs text-status-infoText leading-relaxed">
          <strong>Let op:</strong> De onderstaande tijdlijn is een algemene leidraad.
          Termijnen kunnen in uw situatie afwijken. Raadpleeg altijd een notaris of
          juridisch adviseur voor advies over uw specifieke situatie.
        </p>
      </div>
      <ModuleShell title="Gemeente & overheid"
        description="Tijdlijn van acties na overlijden, plus uw contactgegevens. Door dit nu te ordenen, neemt u uw naasten later veel uitzoekwerk uit handen."
        actions={<SaveStatusBadge status={saveStatus} />}
      >
        {/* Tijdlijn */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Clock className="h-4 w-4 text-sage-600" />
            <h2 className="text-base font-semibold text-ink">Tijdlijn na overlijden</h2>
          </div>
          <div className="relative flex flex-col gap-0">
            {/* Vertical line */}
            <div className="absolute left-[17px] top-6 bottom-6 w-0.5 bg-sand-300 z-0" />
            {TIMELINE.map((phase, pi) => (
              <div key={pi} className="relative flex gap-4 pb-6 last:pb-0">
                <div className={`flex-shrink-0 h-9 w-9 rounded-full border-2 flex items-center justify-center z-10 bg-white border-sand-300`}>
                  <div className={`h-3 w-3 rounded-full ${phase.dot}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`rounded-2xl border px-4 py-4 ${phase.color}`}>
                    <p className="text-sm font-bold mb-2">{phase.period}</p>
                    <ul className="flex flex-col gap-1.5">
                      {phase.items.map((item, ii) => (
                        <li key={ii} className="flex items-start gap-2 text-xs leading-relaxed">
                          <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 opacity-60" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contactgegevens */}
        <Card>
          <h2 className="text-base font-semibold text-ink mb-4">Uw gemeente</h2>
          <div className="flex flex-col gap-4">
            <Input label="Naam gemeente" value={data.municipalityName} onChange={e => upd('municipalityName', e.target.value)} placeholder="Bijv: Gemeente Heiloo" />
            <Input label="Telefoonnummer" value={data.municipalityPhone} onChange={e => upd('municipalityPhone', e.target.value)} placeholder="072-..." />
            <Input label="Website" value={data.municipalityWebsite} onChange={e => upd('municipalityWebsite', e.target.value)} placeholder="www.heiloo.nl" />
          </div>
        </Card>

        {/* Praktisch */}
        <Card>
          <h2 className="text-base font-semibold text-ink mb-4">Praktische gegevens voor naasten</h2>
          <div className="flex flex-col gap-4">
            <Input label="Bank / IBAN" value={data.bankName} onChange={e => upd('bankName', e.target.value)} placeholder="ING, IBAN: NL12..." />
            <Textarea label="Verzekeringen" value={data.insuranceNotes} onChange={e => upd('insuranceNotes', e.target.value)} placeholder="Zorgverzekering bij... polisnr. ..." maxLength={400} />
            <Input label="Pensioenfonds" value={data.pensionFundName} onChange={e => upd('pensionFundName', e.target.value)} placeholder="ABP, APG..." />
            <Input label="Werkgever" value={data.employerName} onChange={e => upd('employerName', e.target.value)} placeholder="Naam werkgever en contactpersoon HR" />
            <Textarea label="Abonnementen op te zeggen" value={data.subscriptionsNotes} onChange={e => upd('subscriptionsNotes', e.target.value)} placeholder="Krant, internet, telefoon, streamingdiensten..." maxLength={400} />
          </div>
        </Card>

        <Card>
          <h2 className="text-base font-semibold text-ink mb-4">Persoonlijke notities</h2>
          <Textarea label="Aanvullende aandachtspunten" value={data.personalNotes}
            onChange={e => upd('personalNotes', e.target.value)}
            placeholder="Bijv: Afvalpas ligt in la, parkeervergunning inleveren bij..." maxLength={600} showCount />
        </Card>
              <div className="flex flex-col gap-2 mt-2">
          <PrimaryActionButton onClick={async () => { await saveNow(); navigate(target) }} icon={<CheckCircle2 className="h-5 w-5" />}>
          {label}
        </PrimaryActionButton>
          <SecondaryActionButton onClick={async () => { await saveNow(); navigate(-1) }}>Terug naar vorige scherm</SecondaryActionButton>
        </div>
    </ModuleShell>
    </div>
  )
}
